import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  GraduationCap,
  Hash,
  Lock,
  Mic,
  Monitor,
  Shield,
  Sun,
  Video,
  Wifi,
  Zap,
} from 'lucide-react';
import EchoHireLogo from '@/components/branding/EchoHireLogo';
import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// ── Static instruction content ────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'prepare',
    title: 'Before you start',
    subtitle: 'Set up your space in 2 minutes',
    steps: [
      {
        icon: Wifi,
        title: 'Stable internet',
        text: 'Use a reliable connection. Close downloads or streaming on the same network.',
      },
      {
        icon: Sun,
        title: 'Quiet, well-lit room',
        text: 'Face the camera with even lighting. Background noise makes transcription less accurate.',
      },
      {
        icon: Monitor,
        title: 'Close other apps',
        text: 'Shut extra browser tabs and apps so your mic and camera work smoothly.',
      },
    ],
  },
  {
    id: 'during',
    title: 'During the interview',
    subtitle: 'How answering works',
    steps: [
      {
        icon: Video,
        title: 'One question at a time',
        text: 'Questions appear one after another. Read each question fully before you record.',
      },
      {
        icon: Mic,
        title: 'Record → Stop → Submit',
        text: (
          <>
            Tap <strong className="text-foreground">Record</strong> to answer, then{' '}
            <strong className="text-foreground">Stop</strong> when finished. Your answer is
            transcribed and submitted automatically.
          </>
        ),
      },
      {
        icon: Zap,
        title: 'Camera & mic required',
        text: 'Keep both enabled for the full session. They are used for proctoring and your responses.',
      },
    ],
  },
  {
    id: 'rules',
    title: 'Important rules',
    subtitle: 'Please read carefully',
    steps: [
      {
        icon: Lock,
        title: 'No going back',
        text: 'You cannot return to a previous question. Each answer is final once you move on.',
        warning: true,
      },
      {
        icon: Shield,
        title: 'Stay on screen',
        text: 'Do not switch tabs or windows. The interview runs fullscreen; leaving may end your session.',
        warning: true,
      },
      {
        icon: AlertTriangle,
        title: 'Proctoring limits',
        text: 'Three infractions (e.g. multiple faces, looking away, no face visible) will end the interview.',
        warning: true,
      },
    ],
  },
];

const TIPS = [
  "Sit at arm's length from the camera",
  'Look at the screen when speaking',
  'Speak clearly at a normal pace',
  'Take a breath before answering each question',
  'Allow camera and microphone when prompted',
];

// ── Comfort tips for candidates ───────────────────────────────────────────────
const COMFORT_TIPS = [
  {
    title: "There's no trick",
    text: "The questions are designed to understand how you think and communicate — not to trip you up. Honest, clear answers are valued over rehearsed ones.",
  },
  {
    title: 'Take your time',
    text: "You have a time limit per question, but use it. A moment's pause to gather your thoughts is perfectly fine.",
  },
  {
    title: "It's okay to say 'I don't know'",
    text: "If you're unsure, say so and explain how you'd approach finding the answer. That shows problem-solving maturity.",
  },
  {
    title: 'Be yourself',
    text: "Authenticity matters. The AI evaluates your reasoning and communication style — there's no single 'right' personality.",
  },
];

async function requestFullscreen() {
  const el = document.documentElement;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
    else if (el.msRequestFullscreen) await el.msRequestFullscreen();
  } catch (err) {
    console.warn('Fullscreen request failed:', err);
  }
}

function StepCard({ icon: Icon, title, text, warning }) {
  return (
    <li
      className={cn(
        'flex gap-3 rounded-lg border px-3 py-2.5 transition-colors',
        warning
          ? 'border-destructive/25 bg-destructive/[0.04]'
          : 'border-border/80 bg-card hover:border-border'
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
          warning ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
        )}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0">
        <p className={cn('text-sm font-semibold', warning && 'text-destructive')}>{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{text}</p>
      </div>
    </li>
  );
}

// ── Role overview card (shown when session data is available) ─────────────────
function RoleOverview({ session }) {
  const [jdOpen, setJdOpen] = useState(false);
  if (!session) return null;

  const t = session.template || {};
  const c = session.candidate || {};
  const firstName = c.firstName || '';
  const role      = t.position || t.title || 'this role';
  const dept      = t.department;
  const duration  = session.durationMinutes || t.durationMinutes;
  const questions = session.numberOfQuestions || t.numberOfQuestions;
  const jd        = t.jobDescription;
  const roundName = session.roundName;
  const difficulty = t.difficultyLevel;

  return (
    <div className="mb-6 rounded-xl border border-primary/20 bg-primary/[0.03] p-4 sm:p-5">
      {/* Greeting */}
      <div className="mb-3">
        {firstName && (
          <p className="text-sm text-muted-foreground">
            Hi <span className="font-semibold text-foreground">{firstName}</span> 👋 — you're all set for
          </p>
        )}
        <h2 className="mt-0.5 text-lg font-bold text-foreground leading-snug">
          {t.title || 'Your Interview'}
        </h2>
        {roundName && (
          <span className="mt-1 inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
            {roundName}
          </span>
        )}
      </div>

      {/* Meta chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {role && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground">
            <Briefcase className="h-3.5 w-3.5 text-primary/70" />
            {role}
          </span>
        )}
        {dept && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground">
            <Building2 className="h-3.5 w-3.5 text-primary/70" />
            {dept}
          </span>
        )}
        {duration && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground">
            <Clock className="h-3.5 w-3.5 text-primary/70" />
            {duration} min
          </span>
        )}
        {questions && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground">
            <Hash className="h-3.5 w-3.5 text-primary/70" />
            {questions} question{questions !== 1 ? 's' : ''}
          </span>
        )}
        {difficulty && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground">
            <GraduationCap className="h-3.5 w-3.5 text-primary/70" />
            {difficulty}
          </span>
        )}
      </div>

      {/* Job description — collapsible */}
      {jd && (
        <div className="border-t border-primary/10 pt-3">
          <button
            type="button"
            onClick={() => setJdOpen(v => !v)}
            className="flex w-full items-center justify-between text-xs font-semibold text-primary/80 hover:text-primary transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Job Description
            </span>
            {jdOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {jdOpen && (
            <div className="mt-2 max-h-52 overflow-y-auto rounded-md border border-primary/10 bg-background/70 p-3">
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground">
                {jd}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Comfort tips section ──────────────────────────────────────────────────────
function ComfortTips() {
  return (
    <div className="mt-5 rounded-xl border border-border bg-card p-4 sm:p-5">
      <h3 className="mb-3 text-sm font-semibold text-foreground">
        💬 A few words before you begin
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {COMFORT_TIPS.map((tip) => (
          <div
            key={tip.title}
            className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
          >
            <p className="text-xs font-semibold text-foreground">{tip.title}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{tip.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
function InstructionsPage({ onBegin, session }) {
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [starting, setStarting] = useState(false);

  const handleBegin = async () => {
    if (!acknowledged || starting) return;
    setStarting(true);
    await requestFullscreen();
    onBegin();
  };

  const questions = session?.numberOfQuestions || session?.template?.numberOfQuestions;
  const duration  = session?.durationMinutes  || session?.template?.durationMinutes;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b border-border/80 bg-card/80 backdrop-blur-sm">
        <div className="mx-auto flex h-12 max-w-5xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <EchoHireLogo size="compact" />
            <button
              onClick={() => navigate('/dashboard')}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Dashboard
            </button>
          </div>
          <div className="flex items-center gap-3">
            {/* Mobile back */}
            <button
              onClick={() => navigate('/dashboard')}
              className="sm:hidden inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Dashboard
            </button>
            <ThemeToggle size="small" />
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        {/* Page header */}
        <header className="mb-6 border-b border-border/60 pb-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Interview setup</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
            Instructions &amp; Overview
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Review your role details and interview guidelines below. Take a moment to read through
            everything before you begin — it only takes a couple of minutes.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              '~5 min read',
              'Fullscreen session',
              'Mic & camera required',
              ...(duration  ? [`${duration} min`]                           : []),
              ...(questions ? [`${questions} question${questions !== 1 ? 's' : ''}`] : []),
            ].map((label) => (
              <span
                key={label}
                className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground"
              >
                {label}
              </span>
            ))}
          </div>
        </header>

        {/* Role overview (only when session data available) */}
        <RoleOverview session={session} />

        {/* Instruction sections */}
        <div className="grid gap-5 lg:grid-cols-3">
          {SECTIONS.map((section) => (
            <section
              key={section.id}
              className="flex flex-col rounded-xl border border-border bg-card shadow-sm"
              aria-labelledby={`section-${section.id}`}
            >
              <div className="border-b border-border/60 px-4 py-3">
                <h2 id={`section-${section.id}`} className="text-sm font-semibold text-foreground">
                  {section.title}
                </h2>
                <p className="text-[11px] text-muted-foreground">{section.subtitle}</p>
              </div>
              <ul className="flex flex-1 flex-col gap-2 p-3">
                {section.steps.map((step) => (
                  <StepCard key={step.title} {...step} />
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* Quick tips */}
        <div className="mt-5 rounded-xl border border-primary/20 bg-primary/[0.04] px-4 py-3 sm:px-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Quick tips</p>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
            {TIPS.map((tip) => (
              <li key={tip} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-primary/80" aria-hidden />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* Comfort tips */}
        <ComfortTips />

        {/* Acknowledgement + begin */}
        <footer className="mt-6 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm leading-snug text-foreground">
              I have read and understand these instructions, including the rules about fullscreen
              mode, proctoring, and not switching tabs.
            </span>
          </label>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              The interview will open in fullscreen. You can exit only after the session ends.
            </p>
            <Button
              size="lg"
              className="h-11 shrink-0 gap-2 px-6"
              disabled={!acknowledged || starting}
              onClick={handleBegin}
            >
              {starting ? 'Starting…' : 'Begin interview'}
              {!starting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default InstructionsPage;
